import React, { useState, ChangeEvent, FC, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Backdrop, Fade, IconButton, FormControl, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import LinearProgress from '@material-ui/core/LinearProgress';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import styles from './MobilePrinterModal.css';
import Button from '../../buttons/Button';
import { mergeClassNames } from '../../../utils';
import RadioButton from '../../buttons/RadioButton';
import MobilePrinterContent from '../../pages/OrderList/MobilePrinter/MobilePrinterContent';
import { useMobilePrinter } from '../../../hooks/useMobilePrinter';
import { MOBILE_PRINTER_TEST_CONNECTION_STATUS } from '../../../constants';
import { getMobilePrinterListFeatureEnabled } from '../../../utils/mobilePrinter';

type Props = {
  isModalOpen: boolean;
  onModalClose: () => void;
};

const useStyles = makeStyles({
  mobilePrinterRoot: {
    width: '100%',
  },
  progressBar: {
    marginTop: '16px',
    width: '100%',
    maxWidth: '200px',
    '& .MuiLinearProgress-barColorPrimary': {
      backgroundColor: 'var(--green)',
    },
    '& .MuiLinearProgress-colorPrimary': {
      backgroundColor: '#e0e0e0',
    },
  },
});

const MobilePrinterModal: FC<Props> = ({ isModalOpen, onModalClose }) => {
  const [printerChoice, setPrinterChoice] = useState<'yes' | 'no' | undefined>();
  const [isClosing, setIsClosing] = useState(false);
  const { t } = useTranslation();
  const translationRoot = 'MOBILE_PRINTER';
  const classes = useStyles();
  const hasFetchedInModalRef = useRef(false);
  const printerInputRef = useRef<HTMLInputElement>(null);
  const mobilePrinterListFeatureEnabled = !!getMobilePrinterListFeatureEnabled();

  const shouldFetchList = useMemo(
    () => printerChoice === 'yes' && !hasFetchedInModalRef.current && mobilePrinterListFeatureEnabled,
    [printerChoice, mobilePrinterListFeatureEnabled]
  );

  const {
    shouldDisableConnectButton,
    manualPrinterNumber,
    isPrinterConnected,
    connectedPrinterNumber,
    mobilePrinterList,
    mobilePrinterListError,
    mobilePrinterListLoading,
    selectedPrinterFromList,
    handleManualPrinterNumberChange,
    handlePrinterSelectionFromList,
    connectPrinter,
    skipPrinterConnection,
    resetToLocalStorageState,
    testConnectionStatus,
    testConnectionMessage,
    testConnectionIsLoading,
    completePrinterConnection,
    resetTestPrinterConnection,
  } = useMobilePrinter({ shouldFetchPrinterList: shouldFetchList, shouldShowToast: false });

  const isSubmitDisabled = !printerChoice || (printerChoice === 'yes' && shouldDisableConnectButton) || testConnectionIsLoading;

  const resetFields = useCallback(() => {
    setPrinterChoice(undefined);
    onModalClose();
    setIsClosing(false);
  }, [onModalClose]);

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as 'yes' | 'no';
    setPrinterChoice(value);
    if (
      value === 'no' &&
      (testConnectionStatus === MOBILE_PRINTER_TEST_CONNECTION_STATUS.warning ||
        testConnectionStatus === MOBILE_PRINTER_TEST_CONNECTION_STATUS.error)
    ) {
      resetTestPrinterConnection();
    }
  };

  const handleSubmit = () => {
    try {
      if (printerChoice === 'yes') {
        connectPrinter();
        // If mobile printer list feature is not enabled, close modal immediately
        if (!mobilePrinterListFeatureEnabled) {
          resetFields();
        }
      } else {
        skipPrinterConnection();
        resetFields();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in handleSubmit:', error);
    }
  };

  const handleCancel = () => {
    skipPrinterConnection();
    resetFields();
  };

  const handleContinue = () => {
    completePrinterConnection();
    resetFields();
  };

  const getSubmitHandler = () => {
    if (isSubmitDisabled) {
      return undefined;
    }
    if (testConnectionStatus === MOBILE_PRINTER_TEST_CONNECTION_STATUS.warning) {
      return handleContinue;
    }
    return handleSubmit;
  };

  const handleCloseOnSuccess = () => {
    completePrinterConnection();
    resetFields();
  };

  useEffect(() => {
    if (isModalOpen) {
      // Sync hook state with localStorage when modal opens in case printer state changed externally
      // Ex: Mobile Printer page
      resetToLocalStorageState();
      hasFetchedInModalRef.current = false;
      setIsClosing(false);
    }
  }, [isModalOpen, resetToLocalStorageState]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (testConnectionStatus === MOBILE_PRINTER_TEST_CONNECTION_STATUS.success && isModalOpen) {
      setIsClosing(true);
      timer = setTimeout(() => {
        completePrinterConnection();
        resetFields();
      }, 1500); // Display message for 1.5 seconds
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [testConnectionStatus, resetFields, isModalOpen, completePrinterConnection]);

  useEffect(() => {
    if (shouldFetchList) {
      hasFetchedInModalRef.current = true;
    }
  }, [shouldFetchList]);

  // Focus input when API fails and manual printer value exists
  useEffect(() => {
    if (testConnectionStatus === MOBILE_PRINTER_TEST_CONNECTION_STATUS.error && manualPrinterNumber && printerInputRef.current) {
      printerInputRef.current.focus();
    }
  }, [testConnectionStatus, manualPrinterNumber]);

  return (
    <Modal
      open={isModalOpen}
      onClose={onModalClose}
      className={styles.modalRoot}
      closeAfterTransition
      disableBackdropClick
      disableEscapeKeyDown
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300 }}
      aria-labelledby="mobile-printer-title"
      aria-describedby="mobile-printer-description"
      style={{ zIndex: 90 }} // overwrite default zIndex of 1300 to display error toast on top of modal
    >
      <Fade in={isModalOpen}>
        {testConnectionStatus === MOBILE_PRINTER_TEST_CONNECTION_STATUS.success ? (
          <div className={styles.modalPaper}>
            <div className={styles.modalHeaderSuccess}>
              <IconButton onClick={handleCloseOnSuccess} className={styles.closeButton} aria-label="Close modal">
                <CloseIcon />
              </IconButton>
            </div>
            <div className={styles.modalSuccessContent}>
              <CheckCircleOutlineOutlinedIcon className={styles.successCheckCircle} />
              <div className={styles.testPrintConnectionSuccess}>{t(`${translationRoot}.TEST_PRINT_SUCCESS`)}</div>
              {isClosing && <LinearProgress className={classes.progressBar} />}
            </div>
          </div>
        ) : (
          <div className={styles.modalPaper} role="dialog" aria-modal="true">
            <div className={styles.modalHeader}>
              <h2 className={styles.modalHeading} id="mobile-printer-title">
                {t(`${translationRoot}.MOBILE_PRINTER_HEADING`)}
              </h2>
              <IconButton onClick={handleCancel} className={styles.closeButton} aria-label="Close modal">
                <CloseIcon />
              </IconButton>
            </div>

            <div className={styles.modalContent} id="mobile-printer-description">
              {testConnectionIsLoading ? (
                <div className={styles.loadingContainer}>
                  <LoadingIndicator />
                </div>
              ) : (
                <>
                  {testConnectionStatus === MOBILE_PRINTER_TEST_CONNECTION_STATUS.error && (
                    <div className={styles.testPrintConnectionError}>{testConnectionMessage}</div>
                  )}

                  {testConnectionStatus === MOBILE_PRINTER_TEST_CONNECTION_STATUS.warning && (
                    <div className={styles.testPrintConnectionWarning}>{testConnectionMessage}</div>
                  )}
                  <FormControl component="fieldset" className={styles.radioGroup}>
                    <div role="radiogroup" aria-label="Printer usage selection">
                      <RadioButton
                        id="printer-no"
                        name="printer-choice"
                        value="no"
                        label={t(`${translationRoot}.DEFAULT_PRINTER`)}
                        checked={printerChoice === 'no'}
                        onChange={handleRadioChange}
                        className={styles.customRadio}
                      />
                      <RadioButton
                        id="printer-yes"
                        name="printer-choice"
                        value="yes"
                        label={t('YES')}
                        checked={printerChoice === 'yes'}
                        onChange={handleRadioChange}
                        className={styles.customRadio}
                      />
                    </div>
                  </FormControl>

                  {printerChoice === 'yes' && (
                    <div className={styles.inputSection}>
                      <MobilePrinterContent
                        ref={printerInputRef}
                        isPrinterConnected={isPrinterConnected}
                        manualPrinterNumber={manualPrinterNumber}
                        handleManualPrinterNumberChange={handleManualPrinterNumberChange}
                        translationRoot={translationRoot}
                        inputRootClass={classes.mobilePrinterRoot}
                        mobilePrinterListError={mobilePrinterListError}
                        mobilePrinterList={mobilePrinterList}
                        mobilePrinterListLoading={mobilePrinterListLoading}
                        handlePrinterSelectionFromList={handlePrinterSelectionFromList}
                        selectedPrinterFromList={selectedPrinterFromList}
                        connectedPrinterNumber={connectedPrinterNumber}
                        mobilePrinterListFeatureEnabled={mobilePrinterListFeatureEnabled}
                        onSubmit={getSubmitHandler()}
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            <div className={styles.modalFooter}>
              <Button
                className={mergeClassNames(styles.btn, styles.cancelButton)}
                data-cy="cancel-button"
                aria-label={t(`${translationRoot}.CANCEL`)}
                onClick={handleCancel}
              >
                {t(`${translationRoot}.CANCEL`)}
              </Button>

              {!testConnectionIsLoading && (
                <>
                  {testConnectionStatus === MOBILE_PRINTER_TEST_CONNECTION_STATUS.warning ? (
                    <Button
                      className={mergeClassNames(styles.btn, styles.submitButton)}
                      data-cy="continue-button"
                      // disabled={isSubmitDisabled}
                      aria-label={t(`${translationRoot}.CONTINUE`)}
                      onClick={handleContinue}
                    >
                      {t(`${translationRoot}.CONTINUE`)}
                    </Button>
                  ) : (
                    <Button
                      className={mergeClassNames(styles.btn, styles.submitButton)}
                      data-cy="submit-button"
                      disabled={isSubmitDisabled}
                      aria-label={t(`${translationRoot}.SUBMIT`)}
                      onClick={handleSubmit}
                    >
                      {t(`${translationRoot}.SUBMIT`)}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </Fade>
    </Modal>
  );
};

export default MobilePrinterModal;
